import React from "react";

import styled from "styled-components";

import { Backdrop } from "Common/Backdrop/Backdrop";

import { AccordionButton } from "./AccordionButton";
import { useHorizontalAccordion } from "./useHorizontalAccordion";

export const CONTENT_WIDTH = "16rem";

interface Props {
  content: React.ReactNode;
  hasFocus?: boolean;
  onChange?: (isOpen: boolean) => void;
}

export const HorizontalAccordion = (props: Props): React.ReactElement => {
  const { content, hasFocus, onChange } = props;
  const { isOpen, onToggle } = useHorizontalAccordion({ onChange });

  return (
    <>
      <Backdrop isActive={!!hasFocus} />

      <HorizontalAccordionLayout>
        <Panel>
          <Content isOpen={isOpen} isActive={hasFocus}>
            {content}
          </Content>
        </Panel>
        <Divider>
          <Shadow isOpen={isOpen} isActive={hasFocus} />
          <ButtonLayout isOpen={isOpen} isActive={hasFocus}>
            <AccordionButton onClick={onToggle} />
          </ButtonLayout>
        </Divider>
      </HorizontalAccordionLayout>
    </>
  );
};

const HorizontalAccordionLayout = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  z-index: 60;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div<{ isOpen: boolean; isActive?: boolean }>`
  width: ${(props) => (props.isOpen ? CONTENT_WIDTH : "0")};
  max-width: ${(props) => (props.isOpen ? CONTENT_WIDTH : "0")};
  background-color: white;
  border-radius: 0.5rem;
  display: block;
  position: relative;
  overflow: hidden;
  transition: width 400ms ease-in-out, max-width 400ms ease-in-out,
    box-shadow 300ms ease-in-out;
  box-shadow: ${(props) =>
    props.isActive ? "4px 4px 8px rgba(0, 0, 0, 0.1)" : "none"};
`;

const Divider = styled.div`
  display: flex;
  flex-direction: column;
`;

const Shadow = styled.div<{ isOpen: boolean; isActive?: boolean }>`
  flex: 1;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0.9rem;
  background: transparent;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  transform: translateX(${(props) => (props.isOpen ? CONTENT_WIDTH : "0")});
  transition: transform 400ms ease-in-out, opacity 300ms ease-in-out;
  overflow: hidden;
  clip-path: inset(0 -10px 0 0);
  opacity: ${(props) => (props.isActive ? "0" : "1")};
`;

const ButtonLayout = styled.div<{ isOpen: boolean; isActive?: boolean }>`
  left: ${(props) => (props.isOpen ? CONTENT_WIDTH : 0)};
  z-index: 60;
  margin-top: 0.75rem;
  transform: rotate(${(props) => (props.isOpen ? 180 : 0)}deg);
  transition: transform 400ms ease-in-out, opacity 300ms ease-in-out;
  opacity: ${(props) => (props.isActive ? "0" : "1")};
`;
